/**
 * 平面 brace を1論理辺まわりに 360° 回した回転体。
 * 輪郭は brace に残し、描画と当たりだけを回す。
 */

import {
  braceCrossSectionBasis,
  braceExtrudeOffset,
  bracePolyline,
  isFillGeometry,
  tessellateBracePolyline,
  vec,
  type MeshData,
} from './geometry';
import type { BraceGeometry, Vec3 } from './types';
import { isPlanar } from './validation';

export const REVOLVE_SEGMENTS = 32;
const AXIS_EPS = 1e-8;
const QUAD_AREA_EPS = 1e-12;

export type RevolveReject =
  | 'need-edge'
  | 'extrude'
  | 'holes'
  | 'cloth'
  | 'not-planar'
  | 'arc-axis'
  | 'too-few';

export function hasRevolve(geometry: BraceGeometry): boolean {
  return geometry.revolve !== undefined;
}

export function revolveAxis(geometry: BraceGeometry): number | null {
  return geometry.revolve?.axis ?? null;
}

export function canRevolve(geometry: BraceGeometry, axis: number): RevolveReject | null {
  const { points, closed } = bracePolyline(geometry);
  const fill = isFillGeometry(geometry);
  const count = fill || closed ? points.length : Math.max(0, points.length - 1);
  if (!Number.isInteger(axis) || axis < 0 || axis >= count) return 'need-edge';
  if (count < 2) return 'too-few';
  if (geometry.arcs?.some((arc) => arc.segment === axis)) return 'arc-axis';
  if (vec.length(braceExtrudeOffset(geometry)) >= 1e-9) return 'extrude';
  if ((geometry.capHoles?.length ?? 0) > 0 || (geometry.holes?.length ?? 0) > 0) return 'holes';
  if (geometry.cloth) return 'cloth';
  const sample = tessellateBracePolyline(geometry).points;
  if (sample.length >= 3 && !isPlanar(sample)) return 'not-planar';
  return null;
}

export function setBraceRevolve(geometry: BraceGeometry, axis: number): BraceGeometry | null {
  if (geometry.revolve?.axis === axis) {
    const next = { ...geometry };
    delete next.revolve;
    return next;
  }
  if (canRevolve(geometry, axis)) return null;
  return { ...geometry, revolve: { axis } };
}

export interface RevolveTriangle {
  a: Vec3;
  b: Vec3;
  c: Vec3;
}

/** 回転体の表面三角形（片面）。当たり判定用。 */
export function revolveSurfaceTriangles(geometry: BraceGeometry): RevolveTriangle[] {
  const rings = latheRings(geometry);
  if (!rings) return [];
  const triangles: RevolveTriangle[] = [];
  for (const quad of rings.quads) {
    const [a, b, c, d] = quad.points;
    if (!a || !b || !c || !d) continue;
    if (triangleArea(a, b, c) >= QUAD_AREA_EPS) triangles.push({ a, b, c });
    if (triangleArea(a, c, d) >= QUAD_AREA_EPS) triangles.push({ a, b: c, c: d });
  }
  return triangles;
}

export function buildRevolveMesh(geometry: BraceGeometry): MeshData {
  const rings = latheRings(geometry);
  const builder = new RevolveMeshBuilder();
  if (!rings) return builder.build();

  for (const quad of rings.quads) {
    addPaperQuad(builder, quad.points, quad.uvs);
  }
  return builder.build();
}

interface LatheUv {
  u: number;
  v: number;
}

interface LatheQuad {
  points: [Vec3, Vec3, Vec3, Vec3];
  uvs: [LatheUv, LatheUv, LatheUv, LatheUv];
}

function latheRings(geometry: BraceGeometry): { quads: LatheQuad[] } | null {
  const axisIndex = geometry.revolve?.axis;
  if (axisIndex === undefined) return null;
  const logical = bracePolyline(geometry);
  const fill = isFillGeometry(geometry);
  const closed = fill || logical.closed;
  const count = closed ? logical.points.length : Math.max(0, logical.points.length - 1);
  if (axisIndex < 0 || axisIndex >= count) return null;
  const axisStart = logical.points[axisIndex];
  const axisEnd = logical.points[(axisIndex + 1) % logical.points.length];
  const axisVec = vec.sub(axisEnd, axisStart);
  const axisLen = vec.length(axisVec);
  if (axisLen < AXIS_EPS) return null;
  const axisDir = vec.scale(axisVec, 1 / axisLen);
  const { right, up } = braceCrossSectionBasis(axisVec, 0);

  const profile = tessellateBracePolyline(geometry);
  const ringClosed = fill || profile.closed;
  const n = profile.points.length;
  if (n < 2) return null;
  const edgeCount = ringClosed ? n : n - 1;

  const heights: number[] = [];
  const radii: number[] = [];
  for (const point of profile.points) {
    const rel = vec.sub(point, axisStart);
    const height = vec.dot(rel, axisDir);
    heights.push(height);
    radii.push(vec.length(vec.sub(rel, vec.scale(axisDir, height))));
  }

  const samples: Vec3[][] = [];
  for (let i = 0; i < n; i++) {
    const ring: Vec3[] = [];
    for (let s = 0; s < REVOLVE_SEGMENTS; s++) {
      const angle = (s / REVOLVE_SEGMENTS) * Math.PI * 2;
      ring.push(rotateAroundAxis(profile.points[i], axisStart, axisDir, angle));
    }
    samples.push(ring);
  }

  const quads: LatheQuad[] = [];
  for (let i = 0; i < edgeCount; i++) {
    const a = profile.points[i];
    const b = profile.points[(i + 1) % n];
    const onA = onAxis(a, axisStart, axisDir);
    const onB = onAxis(b, axisStart, axisDir);
    if (onA && onB) continue;
    const i1 = (i + 1) % n;
    const cap = onA || onB || Math.abs(radii[i1] - radii[i]) >= Math.abs(heights[i1] - heights[i]);
    const wrapRadius = 0.5 * (radii[i] + radii[i1]);
    for (let s = 0; s < REVOLVE_SEGMENTS; s++) {
      const s1 = (s + 1) % REVOLVE_SEGMENTS;
      const p00 = samples[i][s];
      const p01 = samples[i][s1];
      const p10 = samples[(i + 1) % n][s];
      const p11 = samples[(i + 1) % n][s1];
      if (quadArea(p00, p01, p10, p11) < QUAD_AREA_EPS) continue;
      const theta0 = (s / REVOLVE_SEGMENTS) * Math.PI * 2;
      const theta1 = ((s + 1) / REVOLVE_SEGMENTS) * Math.PI * 2;
      const uvs: [LatheUv, LatheUv, LatheUv, LatheUv] = cap
        ? [
            planarUv(p00, axisStart, right, up),
            planarUv(p10, axisStart, right, up),
            planarUv(p11, axisStart, right, up),
            planarUv(p01, axisStart, right, up),
          ]
        : [
            { u: heights[i], v: theta0 * wrapRadius },
            { u: heights[i1], v: theta0 * wrapRadius },
            { u: heights[i1], v: theta1 * wrapRadius },
            { u: heights[i], v: theta1 * wrapRadius },
          ];
      quads.push({ points: [p00, p10, p11, p01], uvs });
    }
  }
  return { quads };
}

function planarUv(point: Vec3, origin: Vec3, right: Vec3, up: Vec3): LatheUv {
  const rel = vec.sub(point, origin);
  return { u: vec.dot(rel, right), v: vec.dot(rel, up) };
}

function onAxis(point: Vec3, origin: Vec3, axis: Vec3): boolean {
  const radial = vec.sub(vec.sub(point, origin), vec.scale(axis, vec.dot(vec.sub(point, origin), axis)));
  return vec.length(radial) < AXIS_EPS;
}

function rotateAroundAxis(point: Vec3, origin: Vec3, axis: Vec3, angle: number): Vec3 {
  const v = vec.sub(point, origin);
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  return vec.add(
    origin,
    vec.add(
      vec.add(vec.scale(v, cos), vec.scale(vec.cross(axis, v), sin)),
      vec.scale(axis, vec.dot(axis, v) * (1 - cos))
    )
  );
}

function faceNormal(a: Vec3, b: Vec3, c: Vec3, d: Vec3): Vec3 {
  const candidates = [
    vec.cross(vec.sub(b, a), vec.sub(c, a)),
    vec.cross(vec.sub(b, a), vec.sub(d, a)),
    vec.cross(vec.sub(c, a), vec.sub(d, a)),
    vec.cross(vec.sub(c, b), vec.sub(d, b)),
  ];
  let best = candidates[0];
  let bestLen = vec.length(best);
  for (let i = 1; i < candidates.length; i++) {
    const len = vec.length(candidates[i]);
    if (len > bestLen) {
      best = candidates[i];
      bestLen = len;
    }
  }
  return vec.normalize(best);
}

function triangleArea(a: Vec3, b: Vec3, c: Vec3): number {
  return vec.length(vec.cross(vec.sub(b, a), vec.sub(c, a)));
}

function quadArea(a: Vec3, b: Vec3, c: Vec3, d: Vec3): number {
  return triangleArea(a, b, c) + triangleArea(a, c, d);
}

function addPaperQuad(
  builder: RevolveMeshBuilder,
  points: [Vec3, Vec3, Vec3, Vec3],
  uvs: [LatheUv, LatheUv, LatheUv, LatheUv]
): void {
  const [a, b, c, d] = points;
  const normal = faceNormal(a, b, c, d);
  if (vec.length(normal) < 1e-9) return;
  const i0 = builder.vertex(a, normal, uvs[0].u, uvs[0].v);
  const i1 = builder.vertex(b, normal, uvs[1].u, uvs[1].v);
  const i2 = builder.vertex(c, normal, uvs[2].u, uvs[2].v);
  const i3 = builder.vertex(d, normal, uvs[3].u, uvs[3].v);
  builder.quad(i0, i1, i2, i3);
  const back = vec.scale(normal, -1);
  const j0 = builder.vertex(a, back, uvs[0].u, uvs[0].v);
  const j1 = builder.vertex(b, back, uvs[1].u, uvs[1].v);
  const j2 = builder.vertex(c, back, uvs[2].u, uvs[2].v);
  const j3 = builder.vertex(d, back, uvs[3].u, uvs[3].v);
  builder.quad(j0, j3, j2, j1);
}

class RevolveMeshBuilder {
  private positions: number[] = [];
  private normals: number[] = [];
  private uvs: number[] = [];
  private indices: number[] = [];

  vertex(p: Vec3, n: Vec3, u: number, v: number): number {
    const index = this.positions.length / 3;
    this.positions.push(p.x, p.y, p.z);
    this.normals.push(n.x, n.y, n.z);
    this.uvs.push(u, v);
    return index;
  }

  quad(a: number, b: number, c: number, d: number): void {
    this.indices.push(a, b, c, a, c, d);
  }

  build(): MeshData {
    return {
      positions: new Float32Array(this.positions),
      normals: new Float32Array(this.normals),
      uvs: new Float32Array(this.uvs),
      indices: new Uint32Array(this.indices),
    };
  }
}
